import { useState } from 'react';
import { updateGame } from '../../services/content/gameService';
import type { Game, FinalResults, GameOutcome, OutcomeSection1, OutcomeSection2, OutcomeSection3, KeyMetric, OutcomeItem } from '../../types/content/game';

type View = 'tabs' | 'metric' | 'item';

interface GameFinalResultsModalProps {
  game: Game;
  onClose: () => void;
  onSuccess: () => void;
}

const emptySection1: OutcomeSection1 = { title: '', score_range: '', outcome_summary: '', prospect_quote: '', key_metrics: [], areas_of_strength: [] };
const emptySection2: OutcomeSection2 = { header: '', items: [] };
const emptySection3: OutcomeSection3 = { header: '', text: '' };
const emptyOutcome: GameOutcome = { section_1: { ...emptySection1 }, section_2: { ...emptySection2 }, section_3: { ...emptySection3 } };

function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

const outcomeKeys: { key: keyof FinalResults; label: string }[] = [
  { key: 'success_outcome', label: 'Success' },
  { key: 'moderate_outcome', label: 'Moderate' },
  { key: 'needs_work_outcome', label: 'Needs Work' },
];

export function GameFinalResultsModal({ game, onClose, onSuccess }: GameFinalResultsModalProps) {
  const initial: FinalResults = game.finalResults
    ? deepClone(game.finalResults)
    : { success_outcome: deepClone(emptyOutcome), moderate_outcome: deepClone(emptyOutcome), needs_work_outcome: deepClone(emptyOutcome) };

  const [results, setResults] = useState<FinalResults>(initial);
  const [activeTab, setActiveTab] = useState<keyof FinalResults>('success_outcome');
  const [view, setView] = useState<View>('tabs');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Sub-editors
  const [editingMetricIndex, setEditingMetricIndex] = useState<number | null>(null);
  const [metricTitle, setMetricTitle] = useState('');
  const [metricDesc, setMetricDesc] = useState('');
  const [editingItemIndex, setEditingItemIndex] = useState<number | null>(null);
  const [itemTitle, setItemTitle] = useState('');
  const [itemDesc, setItemDesc] = useState('');

  const outcome = results[activeTab];

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const btnOutline = 'px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors';
  const btnPrimary = 'px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';
  const btnSmall = 'text-xs px-2 py-1 rounded transition-colors';

  function updateOutcome(updated: GameOutcome) {
    setResults({ ...results, [activeTab]: updated });
  }

  function updateS1(field: keyof OutcomeSection1, value: unknown) {
    const s1 = { ...outcome.section_1, [field]: value };
    updateOutcome({ ...outcome, section_1: s1 });
  }

  function updateS2Field(field: keyof OutcomeSection2, value: unknown) {
    updateOutcome({ ...outcome, section_2: { ...outcome.section_2, [field]: value } });
  }

  function updateS3Field(field: keyof OutcomeSection3, value: unknown) {
    updateOutcome({ ...outcome, section_3: { ...outcome.section_3, [field]: value } });
  }

  // Metric helpers
  function openMetric(index: number | null) {
    if (index !== null) {
      const m = outcome.section_1.key_metrics[index];
      setMetricTitle(m.title);
      setMetricDesc(m.description || '');
    } else {
      setMetricTitle('');
      setMetricDesc('');
    }
    setEditingMetricIndex(index);
    setView('metric');
  }

  function saveMetric() {
    if (!metricTitle.trim()) return;
    const metrics = [...(outcome.section_1.key_metrics || [])];
    const item: KeyMetric = { title: metricTitle.trim(), description: metricDesc.trim() || undefined };
    if (editingMetricIndex !== null) {
      metrics[editingMetricIndex] = item;
    } else {
      metrics.push(item);
    }
    updateS1('key_metrics', metrics);
    setView('tabs');
  }

  function deleteMetric(index: number) {
    updateS1('key_metrics', outcome.section_1.key_metrics.filter((_, i) => i !== index));
  }

  // Section 2 item helpers
  function openItem(index: number | null) {
    if (index !== null) {
      const it = outcome.section_2.items[index];
      setItemTitle(it.title);
      setItemDesc(it.description);
    } else {
      setItemTitle('');
      setItemDesc('');
    }
    setEditingItemIndex(index);
    setView('item');
  }

  function saveItem() {
    if (!itemTitle.trim()) return;
    const items = [...(outcome.section_2.items || [])];
    const it: OutcomeItem = { title: itemTitle.trim(), description: itemDesc.trim() };
    if (editingItemIndex !== null) {
      items[editingItemIndex] = it;
    } else {
      items.push(it);
    }
    updateS2Field('items', items);
    setView('tabs');
  }

  function deleteItem(index: number) {
    updateS2Field('items', outcome.section_2.items.filter((_, i) => i !== index));
  }

  async function handleSave() {
    setError('');
    setLoading(true);
    try {
      await updateGame(game.id, { ...game, finalResults: results });
      onSuccess();
    } catch {
      setError('Failed to update final results');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">

        {view === 'tabs' && (
          <>
            <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Final Results</h2>

              {/* Outcome tabs */}
              <div className="flex gap-1 mb-4 border-b border-gray-200">
                {outcomeKeys.map((o) => (
                  <button
                    key={o.key}
                    onClick={() => setActiveTab(o.key)}
                    className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === o.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>

              <div className="space-y-4">
                {/* Section 1 */}
                <h3 className="text-sm font-semibold text-gray-700 uppercase">Section 1 — Outcome</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Title</label>
                    <input type="text" value={outcome.section_1.title} onChange={(e) => updateS1('title', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Score Range</label>
                    <input type="text" value={outcome.section_1.score_range} onChange={(e) => updateS1('score_range', e.target.value)} className={inputClass} placeholder="e.g. 80-100" />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Outcome Summary</label>
                  <textarea value={outcome.section_1.outcome_summary} onChange={(e) => updateS1('outcome_summary', e.target.value)} className={inputClass} rows={2} />
                </div>
                <div>
                  <label className={labelClass}>Prospect Quote</label>
                  <input type="text" value={outcome.section_1.prospect_quote} onChange={(e) => updateS1('prospect_quote', e.target.value)} className={inputClass} />
                </div>

                {/* Key Metrics */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className={labelClass}>Key Metrics ({outcome.section_1.key_metrics?.length ?? 0})</label>
                    <button type="button" onClick={() => openMetric(null)} className={`${btnSmall} text-blue-600 hover:bg-blue-50`}>+ Add</button>
                  </div>
                  {(outcome.section_1.key_metrics?.length ?? 0) > 0 && (
                    <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                      {outcome.section_1.key_metrics.map((m, i) => (
                        <div key={i} className="flex items-center justify-between px-3 py-2">
                          <span className="text-sm text-gray-700">{m.title}</span>
                          <div className="flex items-center gap-1">
                            <button type="button" onClick={() => openMetric(i)} className={`${btnSmall} text-gray-500 hover:bg-gray-100`}>Edit</button>
                            <button type="button" onClick={() => deleteMetric(i)} className={`${btnSmall} text-red-500 hover:bg-red-50`}>Delete</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className={labelClass}>Areas of Strength (comma-separated)</label>
                  <input
                    type="text"
                    value={(outcome.section_1.areas_of_strength || []).join(', ')}
                    onChange={(e) => updateS1('areas_of_strength', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
                    className={inputClass}
                  />
                </div>

                {/* Section 2 */}
                <h3 className="text-sm font-semibold text-gray-700 uppercase mt-6">Section 2 — Details</h3>
                <div>
                  <label className={labelClass}>Header</label>
                  <input type="text" value={outcome.section_2.header} onChange={(e) => updateS2Field('header', e.target.value)} className={inputClass} />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className={labelClass}>Items ({outcome.section_2.items?.length ?? 0})</label>
                    <button type="button" onClick={() => openItem(null)} className={`${btnSmall} text-blue-600 hover:bg-blue-50`}>+ Add</button>
                  </div>
                  {(outcome.section_2.items?.length ?? 0) > 0 && (
                    <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                      {outcome.section_2.items.map((it, i) => (
                        <div key={i} className="flex items-center justify-between px-3 py-2">
                          <span className="text-sm text-gray-700">{it.title}</span>
                          <div className="flex items-center gap-1">
                            <button type="button" onClick={() => openItem(i)} className={`${btnSmall} text-gray-500 hover:bg-gray-100`}>Edit</button>
                            <button type="button" onClick={() => deleteItem(i)} className={`${btnSmall} text-red-500 hover:bg-red-50`}>Delete</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Section 3 */}
                <h3 className="text-sm font-semibold text-gray-700 uppercase mt-6">Section 3 — Summary</h3>
                <div>
                  <label className={labelClass}>Header</label>
                  <input type="text" value={outcome.section_3.header} onChange={(e) => updateS3Field('header', e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Text</label>
                  <textarea value={outcome.section_3.text} onChange={(e) => updateS3Field('text', e.target.value)} className={inputClass} rows={3} />
                </div>
              </div>
              {error && <p className="text-red-600 text-sm mt-3">{error}</p>}
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button type="button" onClick={onClose} className={btnOutline}>Cancel</button>
              <button type="button" onClick={handleSave} disabled={loading} className={btnPrimary}>{loading ? 'Saving...' : 'Save'}</button>
            </div>
          </>
        )}

        {view === 'metric' && (
          <>
            <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
              <h2 className="text-lg font-bold text-gray-900 mb-4">{editingMetricIndex !== null ? 'Edit Metric' : 'Add Metric'}</h2>
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Title *</label>
                  <input type="text" value={metricTitle} onChange={(e) => setMetricTitle(e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Description</label>
                  <input type="text" value={metricDesc} onChange={(e) => setMetricDesc(e.target.value)} className={inputClass} />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button type="button" onClick={() => setView('tabs')} className={btnOutline}>Back</button>
              <button type="button" onClick={saveMetric} disabled={!metricTitle.trim()} className={btnPrimary}>Done</button>
            </div>
          </>
        )}

        {view === 'item' && (
          <>
            <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
              <h2 className="text-lg font-bold text-gray-900 mb-4">{editingItemIndex !== null ? 'Edit Item' : 'Add Item'}</h2>
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Title *</label>
                  <input type="text" value={itemTitle} onChange={(e) => setItemTitle(e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Description</label>
                  <textarea value={itemDesc} onChange={(e) => setItemDesc(e.target.value)} className={inputClass} rows={2} />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button type="button" onClick={() => setView('tabs')} className={btnOutline}>Back</button>
              <button type="button" onClick={saveItem} disabled={!itemTitle.trim()} className={btnPrimary}>Done</button>
            </div>
          </>
        )}

      </div>
    </div>
  );
}
